import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { TitleText, FlexRowBetween, PageHeader } from 'components'
import { Form, Modal, Checkbox } from 'semantic-ui-react'
import { AppPrimaryButton, FormTextFormField, FormCheckBoxField } from 'elements'
import { X } from 'phosphor-react'
import { QuestionList } from './QuestionList'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { AddEditQuestionModal } from './AddEditQuestion'
import {
    FormErrorText,
    ReorderType,
    TemplateService,
    ChangeAction,
    CommonUtility,
    applyQuestionGuidPatches,
} from 'utility'
import { DesktopMode, MobileMode, useTabletAndBelowMediaQuery } from 'layout'

/**
 * Parent row per nested group (shared ParentId). Same rules as inspection partition:
 * API sends NestedOrder on the parent too — do not use NestedOrder alone.
 */
const nestParentKey = value =>
    value == null || value === '' ? '' : String(value)

const isPersistedTemplateQuestion = question => {
    if (!question || question.ChangeAction === ChangeAction.New) return false
    if (question.Id == null || question.Id === '') return false
    return !String(question.Id).includes('.')
}

function buildTemplateNestedParentIndex(questions) {
    const list = questions || []
    const parentRowByGroupId = new Map()
    list.forEach(q => {
        if (q.ChangeAction === ChangeAction.Delete) return
        const pid = nestParentKey(q.ParentId)
        if (!q.IsNestedQuestion || !pid) return
        const prev = parentRowByGroupId.get(pid)
        if (
            !prev ||
            (q.NestedOrder || 0) < (prev.NestedOrder || 0)
        ) {
            parentRowByGroupId.set(pid, q)
        }
    })
    return parentRowByGroupId
}

function isTemplateNestedFollowUpRow(q, parentRowByGroupId) {
    if (!q || q.ChangeAction === ChangeAction.Delete) return false
    const pid = nestParentKey(q.ParentId)
    if (!pid) return false
    const parentRow = parentRowByGroupId.get(pid)
    if (!parentRow) return false
    return q.Id !== parentRow.Id
}

const mapStoredQuestionToNestedModalNode = q => ({
    Id: q.Id,
    Question: (CommonUtility.strippedHtml(q.Question || '') || '').trim(),
    QuestionType: q.QuestionType,
    ControlResponseId: q.QuestionResponseId,
    MultiResponseType: q.MultiResponseType,
    OptionCount: q.MaxAnswer,
    MinAnswer: q.MinAnswer,
    MaxAnswer: q.MaxAnswer,
    IsRequired: !!q.IsRequired,
    _persisted: true,
    _source: q,
})

const SectionSchema = yup.object().shape({
    SectionName: yup
        .string()
        .trim(FormErrorText.SpacesNotAllowed)
        .strict()
        .required('Section Name is required'),
    SectionURL: yup
        .string()
        .transform(value => (typeof value === 'string' ? value.trim() : ''))
        .nullable(),
})

export const AddEditSectionModal = ({
    open,
    onClose,
    section,
    editMode,
    responseTypes,
    setResponseTypes,
    template,
    templateId,
    templateNumericId,
    startNumber = 1,
    templateSections = [],
    onStampQuestionGuids,
}) => {
    console.log('AddEditSectionModal - TemplateId:', templateId , templateNumericId)

    const [currentQuestion, setCurrentQuestion] = useState(null)
    const [questions, setQuestions] = useState([])
    const [openModal, setOpenModal] = useState(false)
    const [questionEditMode, setQuestionEditMode] = useState(false)
    const [lastResponseType, setLastResponseType] = useState(null)
    const [showQuestionSequenceNumber, setShowQuestionSequenceNumber] =
        useState(false)
    /** Same shape as SectionId (Math.random()) — one id per new nested family */
    const [newNestedFamilyParentId, setNewNestedFamilyParentId] = useState(null)
    const [pendingNestDelete, setPendingNestDelete] = useState(null)

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({ resolver: yupResolver(SectionSchema) })

    const submit = async formData => {
        const sectionObj = {
            ...section,
            ...formData,
            SectionURL:
                typeof formData?.SectionURL === 'string'
                    ? formData.SectionURL.trim()
                    : '',
            SectionUrl:
                typeof formData?.SectionURL === 'string'
                    ? formData.SectionURL.trim()
                    : '',
            IsShowAlways: formData.IsShowAlways !== undefined ? !!formData.IsShowAlways : true,
            IsNAAvailable: !!formData.IsNAAvailable,
            Questions: [...questions].map((ele, i) => ({
                ...ele,
                DisplayOrder: i,
                Order: section.Order,
            })),
        }
        onClose(sectionObj)
    }

    useEffect(() => {
        reset({})
        if (open) {
            if (editMode) {
                reset({
                    ...section,
                    SectionURL:
                        section?.SectionURL ||
                        section?.SectionUrl ||
                        '',
                })
                setQuestions(section.Questions)
            } else {
                reset({})
                setQuestions([])
                setLastResponseType(null) // Reset lastResponseType for new section
            }
        }
    }, [open, editMode, section])

    const addQuestion = () => {
        console.log('Opening question modal with templateId:', templateId)
        setCurrentQuestion(null)
        setQuestionEditMode(false)
        setNewNestedFamilyParentId(Math.random())
        setOpenModal(true)
    }

    const handleResponseTypeChange = responseType => {
        setLastResponseType(responseType)
    }

    const stampQuestionGuids = useCallback(patches => {
        if (!patches?.length) return
        const localIds = new Set((questions || []).map(item => String(item?.Id)))
        const local = patches.filter(item => localIds.has(String(item.Id)))
        const remote = patches.filter(item => !localIds.has(String(item.Id)))
        if (local.length) {
            setQuestions(prev => applyQuestionGuidPatches(prev, local))
        }
        if (remote.length && onStampQuestionGuids) onStampQuestionGuids(remote)
    }, [questions, onStampQuestionGuids])

    const listQuestionsForUi = useMemo(() => {
        const parentIndex = buildTemplateNestedParentIndex(questions)
        return questions.filter(
            q =>
                q.ChangeAction !== ChangeAction.Delete &&
                !isTemplateNestedFollowUpRow(q, parentIndex),
        )
    }, [questions])

    const rootQuestionCount = useMemo(
        () => listQuestionsForUi.length,
        [listQuestionsForUi],
    )

    const onQuestionClose = item => {
        setOpenModal(false)
        if (item) {
            const main = { ...item }
            const childQuestions = main.ChildQuestions || []
            delete main.ChildQuestions

            if (questionEditMode) {
                const previousPid = nestParentKey(currentQuestion?.ParentId)
                const nextPid = nestParentKey(main.ParentId) || previousPid
                const keptIds = new Set(childQuestions.map(child => child.Id))
                const removedPersisted = []
                const withoutOldChildren = questions.filter(q => {
                    const qPid = nestParentKey(q.ParentId)
                    const sameNest =
                        (previousPid && qPid === previousPid) ||
                        (nextPid && qPid === nextPid)
                    if (!sameNest) return true
                    if (q.Id === currentQuestion.Id) return true
                    if (keptIds.has(q.Id)) return false
                    if (q.ChangeAction === ChangeAction.Delete) {
                        removedPersisted.push(q)
                        return false
                    }
                    if (isPersistedTemplateQuestion(q)) {
                        removedPersisted.push({
                            ...q,
                            ChangeAction: ChangeAction.Delete,
                        })
                    }
                    return false
                })
                const replaced = withoutOldChildren.map(x =>
                    x.Id === currentQuestion.Id ? main : x,
                )
                setQuestions([
                    ...replaced,
                    ...childQuestions.map((c, i) => ({
                        ...c,
                        ParentId: nestParentKey(c.ParentId) || nextPid,
                        SectionId: c.SectionId || main.SectionId,
                        Order: c.Order || replaced.length + i + 1,
                    })),
                    ...removedPersisted,
                ])
            } else {
                const nextOrder = questions.length + 1
                setQuestions([
                    ...questions,
                    { ...main, Order: nextOrder },
                    ...childQuestions.map((c, i) => ({
                        ...c,
                        Order: nextOrder + i + 1,
                    })),
                ])
            }
        }
        setCurrentQuestion(null)
        setQuestionEditMode(false)
    }

    const onQuestionNext = item => {
        // Save current question and immediately open new question modal
        if (item) {
            const main = { ...item }
            const childQuestions = main.ChildQuestions || []
            delete main.ChildQuestions
            const nextOrder = questions.length + 1
            setQuestions([
                ...questions,
                { ...main, Order: nextOrder },
                ...childQuestions.map((c, i) => ({
                    ...c,
                    Order: nextOrder + i + 1,
                })),
            ])
        }
        // Keep modal open and reset for new question
        setCurrentQuestion(null)
        setQuestionEditMode(false)
        setNewNestedFamilyParentId(Math.random())
        // The modal should remain open, no need to setOpenModal(true)
        // The AddEditQuestionModal will handle the form reset internally
    }

    const markQuestionsDeleted = ids => {
        const toMark = ids instanceof Set ? ids : new Set(ids)
        setQuestions(
            questions.map((q, i) =>
                toMark.has(q.Id)
                    ? {
                        ...q,
                        ChangeAction: ChangeAction.Delete,
                        DisplayOrder: i,
                    }
                    : { ...q, DisplayOrder: i },
            ),
        )
    }

    const removeQuestion = item => {
        const toMark = new Set([item.Id])
        if (
            item.IsNestedQuestion &&
            item.ParentId != null &&
            item.ParentId !== ''
        ) {
            questions.forEach(q => {
                if (
                    nestParentKey(q.ParentId) === nestParentKey(item.ParentId) &&
                    nestParentKey(item.ParentId) &&
                    q.Id !== item.Id &&
                    q.ChangeAction !== ChangeAction.Delete
                ) {
                    toMark.add(q.Id)
                }
            })
            const childCount = toMark.size - 1
            if (childCount > 0) {
                setPendingNestDelete({
                    ids: [...toMark],
                    childCount,
                })
                return
            }
        }
        markQuestionsDeleted(toMark)
    }

    const editQuestion = item => {
        const enriched = { ...item }
        if (item.ParentId != null && item.ParentId !== '') {
            const nestedFromFlat = questions
                .filter(
                    q =>
                        nestParentKey(q.ParentId) === nestParentKey(item.ParentId) &&
                        nestParentKey(item.ParentId) &&
                        q.Id !== item.Id &&
                        q.ChangeAction !== ChangeAction.Delete,
                )
                .sort(
                    (a, b) => (a.NestedOrder || 0) - (b.NestedOrder || 0),
                )
            if (nestedFromFlat.length > 0) {
                enriched.NestedQuestions =
                    nestedFromFlat.map(mapStoredQuestionToNestedModalNode)
            }
        }
        if (
            (!enriched.NestedQuestions ||
                !Array.isArray(enriched.NestedQuestions) ||
                enriched.NestedQuestions.length === 0) &&
            Array.isArray(item.NestedQuestions) &&
            item.NestedQuestions.length > 0
        ) {
            enriched.NestedQuestions = item.NestedQuestions
        }
        setCurrentQuestion(enriched)
        setQuestionEditMode(true)
        if (item.ParentId == null || item.ParentId === '') {
            setNewNestedFamilyParentId(Math.random())
        }
        setOpenModal(true)
    }

    const onDragCompleted = async reorderedRoots => {
        const parentIndex = buildTemplateNestedParentIndex(questions)
        const nestedRows = questions.filter(
            q =>
                q.ChangeAction !== ChangeAction.Delete &&
                isTemplateNestedFollowUpRow(q, parentIndex),
        )
        const deletedRows = questions.filter(
            q => q.ChangeAction === ChangeAction.Delete,
        )
        const merged = [
            ...reorderedRoots.flatMap(root => {
                const pid = root.ParentId
                if (pid == null || pid === '') return [root]
                const kids = nestedRows
                    .filter(c => nestParentKey(c.ParentId) === nestParentKey(pid))
                    .sort(
                        (a, b) =>
                            (a.NestedOrder || 0) - (b.NestedOrder || 0),
                    )
                return [root, ...kids]
            }),
            ...deletedRows,
        ]
        setQuestions(merged)
        await TemplateService.sort(
            merged
                .filter(q => q.ChangeAction !== ChangeAction.Delete)
                .map((ele, i) => ({
                    DisplayOrder: i,
                    Id: ele.Id,
                    OrderType: ReorderType.Question,
                    TemplateId: template.Id,
                })),
        )
    }
    useTabletAndBelowMediaQuery()

    const nestedFamilyParentIdForModal =
        questionEditMode &&
        currentQuestion != null &&
        currentQuestion.ParentId != null &&
        currentQuestion.ParentId !== ''
            ? currentQuestion.ParentId
            : newNestedFamilyParentId

    return (
        <Modal
            title="Create Questions"
            open={open}
            onCancel={() => onClose()}
            className="model"
            // size="large"
        >
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>
                        {editMode ? 'Edit' : 'Create'} Section
                    </TitleText>
                    <X
                        className="pointer"
                        size={32}
                        onClick={() => onClose()}
                    />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                <Form noValidate>
                    <Form.Field>
                        <FormTextFormField
                            control={control}
                            label="Section name"
                            name="SectionName"
                            placeholder="Section name"
                            errors={errors?.SectionName}
                            defaultValue=""
                        />
                    </Form.Field>
                    <Form.Field>
                        <FormTextFormField
                            control={control}
                            label="Add URL"
                            name="SectionURL"
                            placeholder="Add URL"
                            errors={errors?.SectionURL}
                            defaultValue=""
                        />
                    </Form.Field>
                    <MobileMode>
                        <Form.Field className="mb-3">
                            <FormCheckBoxField
                                control={control}
                                name="CanDelete"
                                label="Allow Delete"
                                defaultValue={!!section?.CanDelete}
                            />
                        </Form.Field>
                        <Form.Field className="mb-3">
                            <FormCheckBoxField
                                control={control}
                                name="IsShowAlways"
                                label="Show Always"
                                defaultValue={section?.IsShowAlways !== undefined ? !!section?.IsShowAlways : true}
                            />
                        </Form.Field>
                        <Form.Field className="mb-3">
                            <FormCheckBoxField
                                control={control}
                                name="IsNAAvailable"
                                label="Can Skip"
                                defaultValue={!!section?.IsNAAvailable}
                            />
                        </Form.Field>
                    </MobileMode>
                    <FlexRowBetween className="align-items-center">
                        <PageHeader header="Questions" />
                        <DesktopMode>
                            <div className="d-flex align-items-center flex-wrap">
                                <span className="d-flex align-items-center me-3">
                                    <FormCheckBoxField
                                        control={control}
                                        name="CanDelete"
                                        label="Allow Delete"
                                        defaultValue={!!section?.CanDelete}
                                    />
                                </span>
                                <span className="d-flex align-items-center me-3">
                                    <FormCheckBoxField
                                        control={control}
                                        name="IsShowAlways"
                                        label="Show Always"
                                        defaultValue={
                                            section?.IsShowAlways !==
                                            undefined
                                                ? !!section?.IsShowAlways
                                                : true
                                        }
                                    />
                                </span>
                                <span className="d-flex align-items-center">
                                    <FormCheckBoxField
                                        control={control}
                                        name="IsNAAvailable"
                                        label="Can Skip"
                                        defaultValue={!!section?.IsNAAvailable}
                                    />
                                </span>
                            </div>
                        </DesktopMode>
                    </FlexRowBetween>
                    <div className="d-flex align-items-center justify-content-end flex-wrap mb-2">
                        <div className="d-flex align-items-center me-3">
                            <span className="me-2">Show sequence number</span>
                            <Checkbox
                                toggle
                                checked={showQuestionSequenceNumber}
                                onChange={(_, { checked }) =>
                                    setShowQuestionSequenceNumber(!!checked)
                                }
                            />
                        </div>
                        <AppPrimaryButton
                            type="button"
                            onClick={addQuestion}
                            className="me-2"
                        >
                            Create Questions
                        </AppPrimaryButton>
                        <AppPrimaryButton
                            type="button"
                            onClick={handleSubmit(submit)}
                        >
                            Submit
                        </AppPrimaryButton>
                    </div>
                    <QuestionList
                        questions={listQuestionsForUi}
                        edit={editQuestion}
                        remove={removeQuestion}
                        onDragCompleted={onDragCompleted}
                        showNumbers={showQuestionSequenceNumber}
                        startNumber={startNumber}
                    />
                    {/* Bottom submit intentionally hidden for all viewports per request */}
                </Form>
                <Modal
                    open={!!pendingNestDelete}
                    size="small"
                    onClose={() => setPendingNestDelete(null)}
                >
                    <Modal.Header>Delete nest</Modal.Header>
                    <Modal.Content>
                        Delete this nest and its {pendingNestDelete?.childCount || 0}{' '}
                        question
                        {pendingNestDelete?.childCount === 1 ? '' : 's'}? The
                        follow-up questions are removed with the header.
                    </Modal.Content>
                    <Modal.Actions>
                        <AppPrimaryButton
                            type="button"
                            onClick={() => setPendingNestDelete(null)}
                            style={{ backgroundColor: '#6b7280', borderColor: '#6b7280' }}
                        >
                            Cancel
                        </AppPrimaryButton>
                        <AppPrimaryButton
                            type="button"
                            onClick={() => {
                                markQuestionsDeleted(pendingNestDelete?.ids || [])
                                setPendingNestDelete(null)
                            }}
                            style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
                        >
                            Delete nest
                        </AppPrimaryButton>
                    </Modal.Actions>
                </Modal>
                <AddEditQuestionModal
                    open={openModal}
                    editMode={questionEditMode}
                    onClose={onQuestionClose}
                    onNext={onQuestionNext}
                    question={currentQuestion}
                    sectionId={section?.SectionId}
                    sectionName={section?.SectionName || ''}
                    setResponseTypes={setResponseTypes}
                    responseTypes={responseTypes}
                    templateId={templateId}
                    templateNumericId={templateNumericId}
                    isFirstQuestion={rootQuestionCount === 0}
                    lastResponseType={lastResponseType}
                    onResponseTypeChange={handleResponseTypeChange}
                    nestedFamilyParentId={nestedFamilyParentIdForModal}
                    sectionQuestions={questions}
                    templateSections={templateSections}
                    onStampQuestionGuids={stampQuestionGuids}
                />
            </Modal.Content>
        </Modal>
    )
}
